import React, { useEffect, useMemo, useState } from "react";
import { Layout, Row, Col, Table, Button, Select, Skeleton, Tag, Typography } from "antd";
import { DownloadOutlined } from "@ant-design/icons";
import HorizontalMenu from "../../../layout/Horizontalmenu";
import { ApiHelper, getUserData } from "../../../../utils/Helpers";
import { ApiRoutes } from "../../../../utils/Config/ApiRoutes";
import Rangepicker from "../../../layout/filter/Rangepicker";
import moment from "moment";

const { Content } = Layout;
const { Text } = Typography;

const statusOptions = [
  { label: "All", value: "" },
  { label: "Received", value: "received" },
  { label: "Confirmed", value: "confirmed" },
  { label: "Rejected", value: "rejected" },
  { label: "Ready", value: "ready" },
  { label: "Preparing", value: "preparing" },
  { label: "Completed", value: "completed" },
  { label: "Cancelled", value: "cancelled" },
  { label: "Delivery", value: "delivery" },

];

const formatMoney = (currencySign, value) => {
  const numericValue = Number(value ?? 0);
  if (Number.isNaN(numericValue)) {
    return `${currencySign}0.00`;
  }
  return `${currencySign}${numericValue.toFixed(2)}`;
};

const statusColor = (event, status) => {
  const normalized = `${event ?? ""}`.toLowerCase();
  if (normalized === "completed" || Number(status) === 5) return "green";
  if (normalized === "confirmed") return "blue";
  if (normalized === "preparing") return "gold";
  if (normalized === "delivery") return "cyan";
  if (normalized === "cancelled") return "red";
  if (normalized === "rejected") return "red";
  if (normalized === "received") return "purple";
  if (normalized === "ready") return "orange";
  return "default";
};


const statusData = (event, status) => {
  const normalized = `${event ?? ""}`.toLowerCase();
  if (normalized === "completed" || Number(status) === 5) return "Completed";
  if (normalized === "confirmed") return "Confirmed";
  if (normalized === "preparing") return "Preparing";
  if (normalized === "delivery") return "Delivery";
  if (normalized === "cancelled") return "Cancelled";
  if (normalized === "rejected") return "Rejected";
  if (normalized === "received") return "Received";
  if (normalized === "ready") return "Ready";
  return event ?? status ?? "-";
}

const OnlineOrderOut = () => {
  const userData = getUserData();
  const lan = userData.language ?? "EN";
  const currencySign = userData.currencySign ?? "$";
  const organizationId = userData.organizationTypeId === 1
    ? userData.parentOrganizationId
    : userData.organizationId;

  const [loading, setLoading] = useState(true);
  const [orders, setOrders] = useState([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [statusFilter, setStatusFilter] = useState("");
  const [storeLocations, setStoreLocations] = useState([]);
  const [selectedStore, setSelectedStore] = useState([]);
  const [filterData, setFilterData] = useState({
    startDate: moment().format("YYYY-MM-DD"),
    endDate: moment().format("YYYY-MM-DD"),
  });
  const [pagination, setPagination] = useState({ current: 1, pageSize: 1000 });

  const loadStoreLocations = async () => {
    const response = await new ApiHelper().FetchFromServer(
      ApiRoutes.GETALLFILTER.service,
      `${ApiRoutes.GETALLFILTER.url}?organizationId=${organizationId}`,
      ApiRoutes.GETALLFILTER.method,
      ApiRoutes.GETALLFILTER.authenticate,
      undefined
    );

    if (response.code === 200) {
      const locations = response.data?.organizationData ?? [];
      const defaultStoreIds = locations.length > 0
        ? locations.map((location) => location.organizationId)
        : [organizationId];
      setStoreLocations(locations);
      setSelectedStore(defaultStoreIds);
      return defaultStoreIds;
    }

    const fallbackStores = [organizationId];
    setSelectedStore(fallbackStores);
    return fallbackStores;
  };

  const fetchOrders = async (
    nextPagination = pagination,
    nextStatus = statusFilter,
    nextStore = selectedStore,
    nextFilterData = filterData
  ) => {
    setLoading(true);
    const organizationParam = Array.isArray(nextStore)
      ? nextStore.join(",")
      : nextStore;
    const response = await new ApiHelper().FetchFromServer(
      ApiRoutes.ORDEROUT_ONLINE_ORDERS.service,
      ApiRoutes.ORDEROUT_ONLINE_ORDERS.url,
      ApiRoutes.ORDEROUT_ONLINE_ORDERS.method,
      ApiRoutes.ORDEROUT_ONLINE_ORDERS.authenticate,
      {
        gmtTime: userData.time_gmt,
        storeOrgId: organizationParam,
        organizationId: userData.organizationId,
        status: nextStatus,
        limit: nextPagination.pageSize,
        offset: nextPagination.current,
        lan,
        startDate: nextFilterData.startDate,
        endDate: nextFilterData.endDate,
      }
    );

    if (response.code === 200) {
      const data = response.data?.data ?? [];
      setOrders(data);
      setTotalRecords(response.data?.total ?? data.length);
    } else {
      setOrders([]);
      setTotalRecords(0);
    }

    setLoading(false);
  };

  useEffect(() => {
    (async () => {
      const defaultStores = await loadStoreLocations();
      fetchOrders(pagination, statusFilter, defaultStores, filterData);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const searchData = (startDate = filterData.startDate, endDate = filterData.endDate) => {
    const nextFilterData = { ...filterData, startDate, endDate };
    setFilterData(nextFilterData);
    setPagination((current) => ({ ...current, current: 1 }));
    fetchOrders({ ...pagination, current: 1 }, statusFilter, selectedStore, nextFilterData);
  };

  const handleStatusChange = (value) => {
    const nextPagination = { ...pagination, current: 1 };
    setStatusFilter(value);
    setPagination(nextPagination);
    fetchOrders(nextPagination, value, selectedStore, filterData);
  };

  const handleStoreChange = (value) => {
    const nextPagination = { ...pagination, current: 1 };
    setSelectedStore(value);
    setPagination(nextPagination);
    fetchOrders(nextPagination, statusFilter, value, filterData);
  };

  const handleTableChange = (nextPagination) => {
    const updatedPagination = {
      current: nextPagination.current,
      pageSize: nextPagination.pageSize,
    };
    setPagination(updatedPagination);
    fetchOrders(updatedPagination, statusFilter, selectedStore, filterData);
  };

  const downloadCsv = () => {
    const csvHeader = [
      "OrderOut Order ID",
      "Customer Name",
      "Customer Phone",
      "Order Type",
      "Marketplace",
      "Items Count",
      "Subtotal",
      "Tax",
      "Delivery Fee",
      "Tip",
      "Total",
      "Status",
      "Created Time",
    ];

    const csvRows = orders.map((order) => [
      order.orderOutOnlineOrderId,
      order.customerName,
      order.customerPhone,
      order.payload?.order?.orderType ?? "",
      order.deliveryCompanyName ?? order.source?.deliveryCompany?.name ?? "",
      order.itemsCount ?? order.items?.length ?? 0,
      order.subtotalAmount,
      order.taxAmount,
      order.deliveryFeeAmount,
      order.tipAmount,
      order.totalAmount,
      order.event,
      order.status,
      order.createDt,
    ]);

    const escapeValue = (value) => `"${`${value ?? ""}`.replace(/"/g, '""')}"`;
    const csvContent = [csvHeader, ...csvRows]
      .map((row) => row.map(escapeValue).join(","))
      .join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", `online-orderout-${moment().format("YYYY-MM-DD_HH-mm")}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  };

  const columns = useMemo(() => ([
    {
      title: "OrderOut Order ID",
      dataIndex: "orderOutOnlineOrderId",
      key: "orderOutOnlineOrderId",
      width: 150,
    },
    {
      title: "Customer Name",
      dataIndex: "customerName",
      key: "customerName",
      width: 180,
    },
    {
      title: "Order Type",
      key: "orderType",
      width: 130,
      render: (_, record) => record.payload?.order?.orderType || "-",
    },
    {
      title: "Marketplace",
      key: "marketplace",
      width: 140,
      render: (_, record) => record.deliveryCompanyName ?? record.source?.deliveryCompany?.name ?? "-",
    },
    {
      title: "Items",
      dataIndex: "itemsCount",
      key: "itemsCount",
      width: 90,
    },
    {
      title: "Subtotal",
      dataIndex: "subtotalAmount",
      key: "subtotalAmount",
      width: 110,
      render: (value) => formatMoney(currencySign, value),
    },
    {
      title: "Tax",
      dataIndex: "taxAmount",
      key: "taxAmount",
      width: 100,
      render: (value) => formatMoney(currencySign, value),
    },
    {
      title: "Delivery Fee",
      dataIndex: "deliveryFeeAmount",
      key: "deliveryFeeAmount",
      width: 120,
      render: (value) => formatMoney(currencySign, value),
    },
    {
      title: "Tip",
      dataIndex: "tipAmount",
      key: "tipAmount",
      width: 100,
      render: (value) => formatMoney(currencySign, value),
    },
    {
      title: "Total",
      dataIndex: "totalAmount",
      key: "totalAmount",
      width: 110,
      render: (value) => formatMoney(currencySign, value),
    },
    {
      title: "Order Status",
      key: "orderStatus",
      width: 140,
      render: (_, record) => (
        <Tag color={statusColor(record.event, record.status)}>
          <span className="capitalize">{statusData(record.event, record.status)}</span>
        </Tag>
      ),
    },
    {
      title: "Created Time",
      dataIndex: "createDt",
      key: "createDt",
      width: 160,
      render: (value) => value ? moment(value).format("YYYY-MM-DD HH:mm") : "-",
    },
  ]), [currencySign]);

  const expandedRowRender = (record) => (
    <div style={{ background: "#fafafa", padding: 16, borderRadius: 8 }}>
      <Row gutter={[16, 16]}>
        <Col xs={24} lg={12}>
          <Text strong>Customer</Text>
          <div>{record.customerName}</div>
          <div>{record.customerPhone}</div>
          <div>{record.customerAddress}</div>
        </Col>
        <Col xs={24} lg={12}>
          <Text strong>Source</Text>
          <div>{record.source?.ods?.name ?? "-"}</div>
          <div>Placed on: {record.source?.placedOn ? moment(record.source.placedOn).format("YYYY-MM-DD HH:mm") : "-"}</div>
          <div>Lead time: {record.source?.leadTime ?? "-"}</div>
        </Col>
        <Col xs={24}>
          <Text strong>Items</Text>
          <div style={{ marginTop: 8 }}>
            {(record.items ?? []).map((item, index) => (
              <div key={`${record.orderOutOnlineOrderId}-${index}`} style={{ padding: "8px 0", borderBottom: index === (record.items?.length ?? 0) - 1 ? "none" : "1px solid #eee" }}>
                <div style={{ display: "flex", justifyContent: "space-between", gap: 12 }}>
                  <div>
                    <Text strong>{item.name}</Text>
                    <div>{item.note || "No note"}</div>
                  </div>
                  <div style={{ textAlign: "right" }}>
                    <div>{item.quantity} x {formatMoney(currencySign, item.price)}</div>
                    <div>{formatMoney(currencySign, item.total)}</div>
                  </div>
                </div>
                {(item.modifiers ?? []).length > 0 && (
                  <div style={{ marginTop: 8, paddingLeft: 16 }}>
                    {item.modifiers.map((modifier, modifierIndex) => (
                      <div key={`${modifier.name}-${modifierIndex}`}>
                        {modifier.quantity} x {modifier.name} {modifier.price ? `(${formatMoney(currencySign, modifier.price)})` : ""}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        </Col>
      </Row>
    </div>
  );

  return (
    <>
      <HorizontalMenu />
      <Content className="content-body" style={{ minHeight: 200 }}>
        <Row>
          <Col lg={24} xs={24}>
            <div
              className="bg-white"
              style={{
                margin: "0px 14px 14px 14px",
                padding: "14px",
                minHeight: 200,
              }}
            >
              <Row className="filterBox" style={{ padding: "0 11px 14px 11px", margin: "0px" }} gutter={{ xs: 12, lg: 6 }}>
                <Col>
                  <Rangepicker
                    filterdata={searchData}
                    startDate={filterData.startDate}
                    endDate={filterData.endDate}
                    lan={lan}
                  />
                </Col>
                {storeLocations.length > 0 && (
                  <Col>
                    <Select
                      mode="multiple"
                      placeholder="Store Location"
                      allowClear
                      showSearch
                      style={{ width: 220 }}
                      maxTagCount={1}
                      value={selectedStore}
                      onChange={handleStoreChange}
                      optionFilterProp="children"
                    >
                      {storeLocations.map((store) => (
                        <Select.Option value={store.organizationId} key={store.organizationId}>
                          {store.organizationName}
                        </Select.Option>
                      ))}
                    </Select>
                  </Col>
                )}
                <Col>
                  <Select
                    placeholder="Order Status"
                    value={statusFilter}
                    onChange={handleStatusChange}
                    style={{ width: 180 }}
                    options={statusOptions}
                  />
                </Col>
                <Col className="ml-auto" style={{ marginLeft: "auto" }}>
                  <Button type="primary" icon={<DownloadOutlined />} onClick={downloadCsv} disabled={orders.length === 0}>
                    Export to CSV
                  </Button>
                </Col>
              </Row>

              <Skeleton loading={loading} active paragraph={{ rows: 6 }}>
                <Table
                  rowKey="orderOutOnlineOrderId"
                  columns={columns}
                  dataSource={orders}
                  pagination={{
                    current: pagination.current,
                    pageSize: pagination.pageSize,
                    total: totalRecords,
                    showSizeChanger: true,
                    pageSizeOptions: ["10", "25", "50", "1000"],
                  }}
                  onChange={handleTableChange}
                  // expandable={{ expandedRowRender }}
                  scroll={{ x: 1600 }}
                />
              </Skeleton>
            </div>
          </Col>
        </Row>
      </Content>
    </>
  );
};

export default OnlineOrderOut;